import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, Calendar, ArrowLeft, ChevronDown } from 'lucide-react';
import statisticsService from '../services/statistics.service';
import authService from '../services/auth.service';

const formatCurrency = (amount) => {
  const absAmount = Math.abs(amount);
  
  if (absAmount >= 1000000000) {
    return `${amount < 0 ? '-' : ''}${(absAmount / 1000000000).toFixed(1)}B`;
  } else if (absAmount >= 1000000) {
    return `${amount < 0 ? '-' : ''}${(absAmount / 1000000).toFixed(1)}M`;
  } else if (absAmount >= 1000) {
    return `${amount < 0 ? '-' : ''}${(absAmount / 1000).toFixed(0)}K`;
  }
  return `${amount < 0 ? '-' : ''}${absAmount.toFixed(0)}`;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1f2c34] border border-[#2a3942] rounded-lg p-3 shadow-lg">
        <p className="text-[#8696a0] text-xs mb-2">{payload[0].payload.period}</p>
        <div className="space-y-1">
          <p className="text-white text-sm font-medium">
            Net Worth: KES {formatCurrency(payload[0].value)}
          </p>
          {payload[1] && (
            <p className="text-[#00a884] text-xs">
              Assets: KES {formatCurrency(payload[1].value)}
            </p>
          )}
          {payload[2] && (
            <p className="text-[#ea4335] text-xs">
              Liabilities: KES {formatCurrency(payload[2].value)}
            </p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function Statistics() {
  const [viewType, setViewType] = useState('months'); // 'months' or 'years'
  const [selectedMonths, setSelectedMonths] = useState('6');
  const [selectedYears, setSelectedYears] = useState('3');
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monthOptions = ['3', '6', '9', '12', '18', '24'];
  const yearOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = authService.getUserId();
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      const value = viewType === 'months' ? selectedMonths : selectedYears;
      const response = await statisticsService.getStatistics(userId, viewType, parseInt(value));
      
      // The API returns { data: [...] }
      setData(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, selectedMonths, selectedYears]);

  const calculateGrowth = () => {
    if (data.length < 2) return { value: 0, percentage: 0 };
    
    const first = data[0].netWorth;
    const last = data[data.length - 1].netWorth;
    const diff = last - first;
    const percentage = first > 0 ? ((diff / first) * 100) : 0;
    
    return { value: diff, percentage };
  };

  const growth = calculateGrowth();
  const isPositive = growth.value >= 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-base mb-4">{error}</p>
          <button 
            onClick={fetchStatistics}
            className="px-4 py-2 bg-[#00a884] text-white rounded-lg hover:bg-[#008f6d] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b141a] pb-20">
      {/* Header */}
      <div className="bg-[#1f2c34] px-4 py-4 lg:py-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => window.history.back()}
              className="w-10 h-10 rounded-full bg-[#2a3942] flex items-center justify-center hover:bg-[#364850] transition-colors">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-white text-xl lg:text-2xl font-normal">Statistics</h1>
              <p className="text-[#8696a0] text-sm">Net worth growth over time</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 mt-6">
        {/* View Type Toggle & Selector */}
        <div className="bg-[#1f2c34] rounded-lg p-4 mb-6">
          {/* Toggle between Months and Years */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setViewType('months')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                viewType === 'months'
                  ? 'bg-[#00a884] text-white'
                  : 'bg-[#2a3942] text-[#8696a0] hover:bg-[#364850]'
              }`}>
              View by Months
            </button>
            <button
              onClick={() => setViewType('years')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                viewType === 'years'
                  ? 'bg-[#00a884] text-white'
                  : 'bg-[#2a3942] text-[#8696a0] hover:bg-[#364850]'
              }`}>
              View by Years
            </button>
          </div>

          {/* Dropdown Selector */}
          {viewType === 'months' ? (
            <div className="relative">
              <button
                onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                className="w-full bg-[#2a3942] text-white px-4 py-3 rounded-lg flex items-center justify-between hover:bg-[#364850] transition-colors">
                <span>Last {selectedMonths} months</span>
                <ChevronDown className="w-5 h-5" />
              </button>
              {showMonthDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#2a3942] rounded-lg shadow-lg overflow-hidden z-20">
                  {monthOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedMonths(option);
                        setShowMonthDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-[#364850] transition-colors ${
                        selectedMonths === option ? 'bg-[#00a884] text-white' : 'text-white'
                      }`}>
                      Last {option} months
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                className="w-full bg-[#2a3942] text-white px-4 py-3 rounded-lg flex items-center justify-between hover:bg-[#364850] transition-colors">
                <span>Last {selectedYears} {selectedYears === '1' ? 'year' : 'years'}</span>
                <ChevronDown className="w-5 h-5" />
              </button>
              {showYearDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#2a3942] rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto z-20">
                  {yearOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedYears(option);
                        setShowYearDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-[#364850] transition-colors ${
                        selectedYears === option ? 'bg-[#00a884] text-white' : 'text-white'
                      }`}>
                      Last {option} {option === '1' ? 'year' : 'years'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Growth Summary Card */}
        <div className="bg-[#1f2c34] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isPositive ? 'bg-[#00a884]/10' : 'bg-[#ea4335]/10'
              }`}>
                {isPositive ? (
                  <TrendingUp className="w-6 h-6 text-[#00a884]" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-[#ea4335]" />
                )}
              </div>
              <div>
                <p className="text-[#8696a0] text-sm">Period Growth</p>
                <p className={`text-2xl font-light ${
                  isPositive ? 'text-[#00a884]' : 'text-[#ea4335]'
                }`}>
                  {isPositive ? '+' : ''}{growth.percentage.toFixed(1)}%
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#8696a0] text-sm">Change</p>
              <p className={`text-lg font-medium ${
                isPositive ? 'text-[#00a884]' : 'text-[#ea4335]'
              }`}>
                {isPositive ? '+' : ''}KES {formatCurrency(growth.value)}
              </p>
            </div>
          </div>

          {/* Current Stats */}
          {data.length > 0 && (
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#2a3942]">
              <div>
                <p className="text-[#8696a0] text-xs mb-1">Net Worth</p>
                <p className="text-white text-sm font-medium">
                  KES {formatCurrency(data[data.length - 1].netWorth)}
                </p>
              </div>
              <div>
                <p className="text-[#8696a0] text-xs mb-1">Assets</p>
                <p className="text-[#00a884] text-sm font-medium">
                  KES {formatCurrency(data[data.length - 1].assets)}
                </p>
              </div>
              <div>
                <p className="text-[#8696a0] text-xs mb-1">Liabilities</p>
                <p className="text-[#ea4335] text-sm font-medium">
                  KES {formatCurrency(data[data.length - 1].liabilities)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Net Worth Chart */}
        <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6 mb-6">
          <h2 className="text-white text-lg font-normal mb-4">Net Worth Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00a884" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#00a884" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3942" />
              <XAxis 
                dataKey="period" 
                stroke="#8696a0" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#8696a0" 
                style={{ fontSize: '12px' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="netWorth"
                stroke="#00a884"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorNetWorth)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Assets vs Liabilities Chart */}
        <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6">
          <h2 className="text-white text-lg font-normal mb-4">Assets vs Liabilities</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3942" />
              <XAxis 
                dataKey="period" 
                stroke="#8696a0" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#8696a0" 
                style={{ fontSize: '12px' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="netWorth"
                stroke="#ffffff"
                strokeWidth={3}
                dot={{ fill: '#ffffff', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="assets"
                stroke="#00a884"
                strokeWidth={2}
                dot={{ fill: '#00a884', r: 3 }}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="liabilities"
                stroke="#ea4335"
                strokeWidth={2}
                dot={{ fill: '#ea4335', r: 3 }}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white"></div>
              <span className="text-[#8696a0]">Net Worth</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#00a884]"></div>
              <span className="text-[#8696a0]">Assets</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#ea4335]"></div>
              <span className="text-[#8696a0]">Liabilities</span>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-[#1f2c34] rounded-lg p-4 border-l-4 border-[#00a884]">
          <div className="flex gap-3">
            <Calendar className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium mb-1">Track Your Progress</p>
              <p className="text-[#8696a0] text-xs leading-relaxed">
                Your net worth is calculated as total assets minus total liabilities. 
                Keep adding assets and paying down liabilities to grow your wealth over time.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}